import React, { useEffect, useMemo, useRef, useState } from 'react';
import StickerCell from './StickerCell';
import { ALBUM_SECTIONS } from '../albumData';
import { getSectionCountries } from '../albumDecorations';

/* ──────────────────────────────────────────────────────────────────────
   Helpers — duplicated locally so the file is self-contained.
   ────────────────────────────────────────────────────────────────────── */

function isHidden(n) { return n >= 995 && n <= 1000; }
function sliceRange(stickers, from, to) {
  return stickers.filter(s => s.n >= from && s.n <= to && !isHidden(s.n));
}
function sliceSection(stickers, section) {
  return sliceRange(stickers, section.from, section.to);
}
function rangeParticipates(participating, from, to) {
  if (!participating || participating.size === 0) return true;
  for (const n of participating) if (n >= from && n <= to) return true;
  return false;
}
function ownedInSlice(slice) { return slice.filter(s => s.owned).length; }

/** Build a flat list of "country entries" — one per country, with FWC/CC as their own entries. */
function buildCountryEntries(stickers, participating) {
  return ALBUM_SECTIONS.flatMap(section => {
    const countries = getSectionCountries(section);
    if (!countries) {
      if (!rangeParticipates(participating, section.from, section.to)) return [];
      return [{ kind: 'fwc', section, slice: sliceSection(stickers, section) }];
    }
    return countries
      .filter(c => rangeParticipates(participating, c.from, c.to))
      .map(c => ({ kind: 'country', country: c, slice: sliceRange(stickers, c.from, c.to), section }));
  });
}

/** Build a list of group entries (FWC, A..L, CC). Each contains either countries or a flat slice. */
function buildGroupEntries(stickers, participating) {
  return ALBUM_SECTIONS
    .filter(section => rangeParticipates(participating, section.from, section.to))
    .map(section => {
      const countries = getSectionCountries(section);
      const slice = sliceSection(stickers, section);
      if (!countries) return { kind: 'flat', section, slice };
      return {
        kind: 'group',
        section,
        slice,
        countries: countries.map(c => ({ ...c, slice: sliceRange(stickers, c.from, c.to) })),
      };
    });
}

/* ──────────────────────────────────────────────────────────────────────
   Reusable building blocks
   ────────────────────────────────────────────────────────────────────── */

function StickerGrid({ slice, columns, gap = 3, padding = 'p-2', cellProps, theme }) {
  return (
    <div
      className={padding}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap,
        backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.4)',
      }}
    >
      {slice.map(s => (
        <StickerCell key={s.n} sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
      ))}
    </div>
  );
}

function CountryHeaderInline({ entry, muted, dense = false, large = false }) {
  const slice = entry.slice;
  const owned = ownedInSlice(slice);
  const total = slice.length;
  const pct = total === 0 ? 0 : Math.round((owned / total) * 100);
  const color = entry.kind === 'country' ? entry.country.color : '#6b7280';
  const code = entry.kind === 'country' ? entry.country.code : entry.section.label;
  const name = entry.kind === 'country' ? entry.country.name : entry.section.subtitle;
  const padY = dense ? 'py-1' : large ? 'py-2.5' : 'py-2';
  return (
    <div className={`flex items-center gap-2 px-3 ${padY}`}>
      <span className="rounded-sm shrink-0" style={{ backgroundColor: color, width: 4, height: large ? 24 : 18 }} />
      <span className={`font-black uppercase tracking-wide truncate ${large ? 'text-base' : 'text-[11px]'}`} style={{ color }}>{code}</span>
      <span className={`font-medium truncate ${large ? 'text-sm' : 'text-[11px]'}`} style={{ color: muted }}>{name}</span>
      <span className="ml-auto text-[10px] tabular-nums shrink-0" style={{ color: muted }}>{owned}/{total} · {pct}%</span>
    </div>
  );
}

/* Country card — full width on mobile, compact stickers grid inside. */
function CountryFullCard({ entry, theme, cellProps, muted, columns = 5 }) {
  const cardCode = entry.kind === 'country' ? entry.country.code : entry.section.label;
  return (
    <div
      data-album-card={cardCode}
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: theme?.gridBg ?? '#ffffff', border: `1px solid ${muted}22` }}
    >
      <div className="h-1 w-full" style={{ backgroundColor: entry.kind === 'country' ? entry.country.color : '#6b7280' }} />
      <CountryHeaderInline entry={entry} muted={muted} />
      <StickerGrid slice={entry.slice} columns={columns} gap={3} padding="px-3 pb-3" cellProps={cellProps} theme={theme} />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   A — Two-up Country Cards (mobile-cards-2)
   ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
   A0 — One Country per Row (mobile-cards-1)
   La layout original que teníamos antes de probar otras alternativas: un
   país por fila, full-width, 5×4 sticker grid adentro. La más espaciosa.
   ────────────────────────────────────────────────────────────────────── */

function MobileOneColCards({ stickers, theme, cellProps, accent, muted, participating, searchQuery }) {
  const entries = useMemo(
    () => buildCountryEntries(stickers, participating).filter(e => matchesSearch(e, searchQuery)),
    [stickers, participating, searchQuery]
  );
  return (
    <div className="px-2 py-3 flex flex-col gap-3">
      {entries.map((entry, i) => (
        <CountryFullCard
          key={i}
          entry={entry}
          theme={theme}
          cellProps={cellProps}
          muted={muted}
          columns={5}
        />
      ))}
    </div>
  );
}

function matchesSearch(entry, query) {
  if (!query) return true;
  const q = String(query).trim().toLowerCase();
  if (!q) return true;
  const code = (entry.kind === 'country' ? entry.country.code : entry.section.label).toLowerCase();
  const name = (entry.kind === 'country' ? entry.country.name : entry.section.subtitle).toLowerCase();
  return code.includes(q) || name.includes(q);
}

function MobileTwoColCards({ stickers, theme, cellProps, accent, muted, participating, searchQuery }) {
  const entries = useMemo(
    () => buildCountryEntries(stickers, participating).filter(e => matchesSearch(e, searchQuery)),
    [stickers, participating, searchQuery]
  );
  return (
    <div className="px-2 py-3 grid grid-cols-2 gap-2">
      {entries.map((entry, i) => {
        const slice = entry.slice;
        const owned = ownedInSlice(slice);
        const total = slice.length;
        const pct = total ? Math.round((owned / total) * 100) : 0;
        const color = entry.kind === 'country' ? entry.country.color : accent;
        const code = entry.kind === 'country' ? entry.country.code : entry.section.label;
        return (
          <div key={i} className="rounded-lg overflow-hidden" style={{ backgroundColor: theme?.gridBg ?? '#fff', border: `1px solid ${muted}22` }}>
            <div className="h-1" style={{ backgroundColor: color }} />
            <div className="px-2 py-1.5 flex items-baseline justify-between gap-1">
              <span className="text-[12px] font-black tracking-wide truncate" style={{ color }}>{code}</span>
              <span className="text-[9px] tabular-nums shrink-0" style={{ color: muted }}>{owned}/{total} · {pct}%</span>
            </div>
            <StickerGrid slice={slice} columns={5} gap={2} padding="px-2 pb-2" cellProps={cellProps} theme={theme} />
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   A2 — Two-up Country Cards COMPACTAS (mobile-cards-2-tight)
   Misma idea que A pero con márgenes/gaps recortados para que los
   cuadraditos respiren un poco más.
   ────────────────────────────────────────────────────────────────────── */

function MobileTwoColCardsTight({ stickers, theme, cellProps, accent, muted, participating, searchQuery }) {
  const entries = useMemo(
    () => buildCountryEntries(stickers, participating).filter(e => matchesSearch(e, searchQuery)),
    [stickers, participating, searchQuery]
  );
  return (
    <div className="px-1 py-2 grid grid-cols-2 gap-1">
      {entries.map((entry, i) => {
        const slice = entry.slice;
        const owned = ownedInSlice(slice);
        const total = slice.length;
        const pct = total ? Math.round((owned / total) * 100) : 0;
        const color = entry.kind === 'country' ? entry.country.color : accent;
        const code = entry.kind === 'country' ? entry.country.code : entry.section.label;
        return (
          <div key={i} className="rounded-lg overflow-hidden" style={{ backgroundColor: theme?.gridBg ?? '#fff', border: `1px solid ${muted}22` }}>
            <div className="h-1" style={{ backgroundColor: color }} />
            <div className="px-1.5 py-1 flex items-baseline justify-between gap-1">
              <span className="text-[12px] font-black tracking-wide truncate" style={{ color }}>{code}</span>
              <span className="text-[9px] tabular-nums shrink-0" style={{ color: muted }}>{owned}/{total} · {pct}%</span>
            </div>
            <StickerGrid slice={slice} columns={5} gap={2} padding="px-1.5 pb-1.5" cellProps={cellProps} theme={theme} />
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   A3 — Two-up Country Cards EXTRA-COMPACTAS (mobile-cards-2-xtight)
   Aún menos separación entre cards y márgenes para que cada figurita gane
   ~3 px de ancho contra A2.
   ────────────────────────────────────────────────────────────────────── */

function MobileTwoColCardsXtight({ stickers, theme, cellProps, accent, muted, participating, searchQuery }) {
  const entries = useMemo(
    () => buildCountryEntries(stickers, participating).filter(e => matchesSearch(e, searchQuery)),
    [stickers, participating, searchQuery]
  );
  return (
    <div className="px-0.5 py-1.5 grid grid-cols-2 gap-px">
      {entries.map((entry, i) => {
        const slice = entry.slice;
        const owned = ownedInSlice(slice);
        const total = slice.length;
        const color = entry.kind === 'country' ? entry.country.color : accent;
        const code = entry.kind === 'country' ? entry.country.code : entry.section.label;
        return (
          <div key={i} data-album-card={code} className="rounded-md overflow-hidden" style={{ backgroundColor: theme?.gridBg ?? '#fff' }}>
            <div className="h-0.5" style={{ backgroundColor: color }} />
            <div className="px-1 py-0.5 flex items-baseline justify-between gap-1">
              <span className="text-[11px] font-black tracking-wide truncate" style={{ color }}>{code}</span>
              <span className="text-[8px] tabular-nums shrink-0" style={{ color: muted }}>{owned}/{total}</span>
            </div>
            <StickerGrid slice={slice} columns={5} gap={2} padding="px-1 pb-1" cellProps={cellProps} theme={theme} />
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   B — Sticky group tabs + 1-col cards (mobile-tabs-cards)
   ────────────────────────────────────────────────────────────────────── */

function MobileTabsCards({ stickers, theme, cellProps, accent, muted, participating }) {
  const groups = useMemo(() => buildGroupEntries(stickers, participating), [stickers, participating]);
  const refs = useRef({});
  const [active, setActive] = useState(groups[0]?.section.id ?? null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.getAttribute('data-section-id');
          if (id) setActive(id);
        }
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.25, 0.5, 1] }
    );
    Object.values(refs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [groups]);

  const scrollTo = (id) => {
    const el = refs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      <div className="sticky top-0 z-20 -mx-3 mobile-md:-mx-6 px-3 mobile-md:px-6 py-2 flex gap-1.5 overflow-x-auto hide-scrollbar bg-white/95 backdrop-blur-sm border-b" style={{ borderColor: `${muted}33` }}>
        {groups.map(g => {
          const isActive = active === g.section.id;
          return (
            <button
              key={g.section.id}
              onClick={() => scrollTo(g.section.id)}
              className="shrink-0 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest transition-colors"
              style={{
                backgroundColor: isActive ? accent : `${accent}1a`,
                color: isActive ? '#fff' : accent,
              }}
            >
              {g.section.label}
            </button>
          );
        })}
      </div>
      <div className="px-2 py-3 flex flex-col gap-3">
        {groups.map(g => (
          <div
            key={g.section.id}
            ref={(el) => { refs.current[g.section.id] = el; }}
            data-section-id={g.section.id}
            style={{ scrollMarginTop: 56 }}
          >
            <div className="px-1 pb-1 flex items-baseline gap-2">
              <span className="px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-widest" style={{ backgroundColor: `${accent}22`, color: accent }}>
                {g.section.label}
              </span>
              <span className="text-[11px] truncate" style={{ color: muted }}>{g.section.subtitle}</span>
            </div>
            {g.kind === 'group' ? (
              <div className="flex flex-col gap-2">
                {g.countries.map(c => (
                  <CountryFullCard
                    key={c.code}
                    entry={{ kind: 'country', country: c, slice: c.slice, section: g.section }}
                    theme={theme}
                    cellProps={cellProps}
                    muted={muted}
                  />
                ))}
              </div>
            ) : (
              <CountryFullCard
                entry={{ kind: 'fwc', section: g.section, slice: g.slice }}
                theme={theme}
                cellProps={cellProps}
                muted={muted}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   C — Doble selector (group + country) → 1 país grande (mobile-double)
   ────────────────────────────────────────────────────────────────────── */

function MobileDoubleSelector({ stickers, theme, cellProps, accent, muted, participating }) {
  const groups = useMemo(() => buildGroupEntries(stickers, participating), [stickers, participating]);
  const [groupId, setGroupId] = useState(groups[0]?.section.id ?? null);
  const [countryCode, setCountryCode] = useState(null);
  const [groupSheet, setGroupSheet] = useState(false);

  const activeGroup = groups.find(g => g.section.id === groupId) || groups[0];
  const activeEntry = (() => {
    if (!activeGroup) return null;
    if (activeGroup.kind === 'flat') return { kind: activeGroup.section.id === 'FWC' ? 'fwc' : 'fwc', section: activeGroup.section, slice: activeGroup.slice };
    const c = activeGroup.countries.find(x => x.code === countryCode) || activeGroup.countries[0];
    return c ? { kind: 'country', country: c, slice: c.slice, section: activeGroup.section } : null;
  })();

  if (!activeEntry) return null;

  return (
    <div className="px-3 pt-3 pb-4">
      <button
        onClick={() => setGroupSheet(true)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white"
        style={{ borderColor: `${muted}33` }}
      >
        <span className="text-xs font-black uppercase tracking-widest" style={{ color: muted }}>{`Grupo`}</span>
        <span className="text-base font-black" style={{ color: accent }}>{activeGroup.section.label}</span>
        <span className="ml-auto text-xs font-medium truncate" style={{ color: muted }}>{activeGroup.section.subtitle}</span>
        <span className="material-symbols-outlined text-base shrink-0" style={{ color: muted }}>expand_more</span>
      </button>

      {activeGroup.kind === 'group' && (
        <div className="mt-2 grid grid-cols-4 gap-1.5">
          {activeGroup.countries.map(c => {
            const isActive = (activeEntry.kind === 'country' && activeEntry.country.code === c.code);
            const owned = ownedInSlice(c.slice);
            return (
              <button
                key={c.code}
                onClick={() => setCountryCode(c.code)}
                className="flex flex-col items-center gap-0.5 px-1 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: isActive ? c.color : `${c.color}11`,
                  border: `1.5px solid ${isActive ? c.color : 'transparent'}`,
                  color: isActive ? '#fff' : c.color,
                }}
              >
                <span className="text-[12px] font-black tracking-wide leading-none">{c.code}</span>
                <span className="text-[9px] tabular-nums opacity-90 leading-none">{owned}/{c.slice.length}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-3 rounded-2xl overflow-hidden border" style={{ backgroundColor: theme?.gridBg ?? '#fff', borderColor: `${muted}22` }}>
        <div className="h-1.5 w-full" style={{ backgroundColor: activeEntry.kind === 'country' ? activeEntry.country.color : accent }} />
        <CountryHeaderInline entry={activeEntry} muted={muted} large />
        <StickerGrid slice={activeEntry.slice} columns={5} gap={6} padding="px-4 pb-4" cellProps={cellProps} theme={theme} />
      </div>

      {groupSheet && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-end" onClick={() => setGroupSheet(false)}>
          <div
            className="w-full max-h-[70vh] overflow-y-auto rounded-t-3xl bg-white p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1.5 rounded-full bg-gray-300 mx-auto mb-3" />
            <p className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-3">Elegí un grupo</p>
            <div className="grid grid-cols-2 gap-2">
              {groups.map(g => {
                const owned = ownedInSlice(g.slice);
                const total = g.slice.length;
                const pct = total ? Math.round((owned / total) * 100) : 0;
                const isActive = g.section.id === activeGroup.section.id;
                return (
                  <button
                    key={g.section.id}
                    onClick={() => { setGroupId(g.section.id); setCountryCode(null); setGroupSheet(false); }}
                    className="flex flex-col gap-1 p-3 rounded-xl border text-left"
                    style={{
                      backgroundColor: isActive ? `${accent}11` : '#fff',
                      borderColor: isActive ? accent : `${muted}33`,
                    }}
                  >
                    <span className="text-sm font-black" style={{ color: isActive ? accent : '#1f2937' }}>{g.section.label}</span>
                    <span className="text-[10px] truncate" style={{ color: muted }}>{g.section.subtitle}</span>
                    <span className="text-[10px] tabular-nums" style={{ color: muted }}>{owned}/{total} · {pct}%</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   D — Carrusel vertical snap (mobile-snap-y)
   ────────────────────────────────────────────────────────────────────── */

function MobileSnapVertical({ stickers, theme, cellProps, accent, muted, participating }) {
  const groups = useMemo(() => buildGroupEntries(stickers, participating), [stickers, participating]);
  const containerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const top = el.scrollTop;
      const h = el.clientHeight;
      const idx = Math.round(top / h);
      setActiveIdx(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [groups]);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        className="overflow-y-auto hide-scrollbar"
        style={{
          height: 'calc(100vh - 220px)',
          scrollSnapType: 'y mandatory',
        }}
      >
        {groups.map((g, idx) => (
          <section
            key={g.section.id}
            style={{ scrollSnapAlign: 'start', minHeight: 'calc(100vh - 220px)' }}
            className="px-3 py-3"
          >
            <div className="flex items-baseline gap-2 px-1 pb-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest" style={{ backgroundColor: `${accent}22`, color: accent }}>
                {g.section.label}
              </span>
              <span className="text-[11px] truncate" style={{ color: muted }}>{g.section.subtitle}</span>
              <span className="ml-auto text-[10px] tabular-nums shrink-0" style={{ color: muted }}>
                {ownedInSlice(g.slice)}/{g.slice.length}
              </span>
            </div>
            {g.kind === 'group' ? (
              <div className="grid grid-cols-2 gap-2">
                {g.countries.map(c => (
                  <CountryFullCard
                    key={c.code}
                    entry={{ kind: 'country', country: c, slice: c.slice, section: g.section }}
                    theme={theme}
                    cellProps={cellProps}
                    muted={muted}
                    columns={5}
                  />
                ))}
              </div>
            ) : (
              <CountryFullCard
                entry={{ kind: 'fwc', section: g.section, slice: g.slice }}
                theme={theme}
                cellProps={cellProps}
                muted={muted}
              />
            )}
          </section>
        ))}
      </div>
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1.5">
        {groups.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all"
            style={{
              width: i === activeIdx ? 8 : 4,
              height: i === activeIdx ? 8 : 4,
              backgroundColor: i === activeIdx ? accent : `${muted}66`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   E — Pestañas swipeables (mobile-swipe-x)
   ────────────────────────────────────────────────────────────────────── */

function MobileSwipeHorizontal({ stickers, theme, cellProps, accent, muted, participating }) {
  const groups = useMemo(() => buildGroupEntries(stickers, participating), [stickers, participating]);
  const containerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const left = el.scrollLeft;
      const w = el.clientWidth;
      const idx = Math.round(left / w);
      setActiveIdx(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [groups]);

  const goTo = (idx) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <div className="pt-2 pb-4">
      <div className="flex items-center justify-between px-3 pb-2">
        <button
          onClick={() => goTo(Math.max(0, activeIdx - 1))}
          disabled={activeIdx === 0}
          className="w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-30"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <span className="material-symbols-outlined text-base">chevron_left</span>
        </button>
        <div className="flex flex-col items-center min-w-0">
          <span className="text-sm font-black" style={{ color: accent }}>{groups[activeIdx]?.section.label}</span>
          <span className="text-[10px] truncate max-w-[200px]" style={{ color: muted }}>{groups[activeIdx]?.section.subtitle}</span>
        </div>
        <button
          onClick={() => goTo(Math.min(groups.length - 1, activeIdx + 1))}
          disabled={activeIdx === groups.length - 1}
          className="w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-30"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <span className="material-symbols-outlined text-base">chevron_right</span>
        </button>
      </div>

      <div
        ref={containerRef}
        className="flex overflow-x-auto hide-scrollbar"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {groups.map(g => (
          <div
            key={g.section.id}
            className="shrink-0 w-full px-3"
            style={{ scrollSnapAlign: 'start' }}
          >
            {g.kind === 'group' ? (
              <div className="flex flex-col gap-2">
                {g.countries.map(c => (
                  <CountryFullCard
                    key={c.code}
                    entry={{ kind: 'country', country: c, slice: c.slice, section: g.section }}
                    theme={theme}
                    cellProps={cellProps}
                    muted={muted}
                  />
                ))}
              </div>
            ) : (
              <CountryFullCard
                entry={{ kind: 'fwc', section: g.section, slice: g.slice }}
                theme={theme}
                cellProps={cellProps}
                muted={muted}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-center items-center gap-1.5 pt-3">
        {groups.map((g, i) => (
          <button
            key={g.section.id}
            onClick={() => goTo(i)}
            className="rounded-full transition-all"
            style={{
              width: i === activeIdx ? 18 : 6,
              height: 6,
              backgroundColor: i === activeIdx ? accent : `${muted}55`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   F — Strip compacto (1 fila por país, scroll-x interno) (mobile-strips)
   ────────────────────────────────────────────────────────────────────── */

function MobileStrips({ stickers, theme, cellProps, accent, muted, participating }) {
  const entries = useMemo(() => buildCountryEntries(stickers, participating), [stickers, participating]);
  return (
    <div className="px-2 py-2 flex flex-col gap-1">
      {entries.map((entry, i) => {
        const slice = entry.slice;
        const owned = ownedInSlice(slice);
        const total = slice.length;
        const pct = total ? Math.round((owned / total) * 100) : 0;
        const color = entry.kind === 'country' ? entry.country.color : accent;
        const code = entry.kind === 'country' ? entry.country.code : entry.section.label;
        return (
          <div
            key={i}
            className="rounded-lg flex items-center gap-2 px-2 py-1"
            style={{ backgroundColor: theme?.gridBg ?? 'rgba(255,255,255,0.6)', border: `1px solid ${muted}1a` }}
          >
            <div className="flex flex-col items-start shrink-0 w-[78px]">
              <div className="flex items-center gap-1">
                <span className="rounded-sm shrink-0" style={{ backgroundColor: color, width: 3, height: 14 }} />
                <span className="text-[11px] font-black tracking-wide" style={{ color }}>{code}</span>
              </div>
              <span className="text-[9px] tabular-nums" style={{ color: muted }}>{owned}/{total} · {pct}%</span>
            </div>
            <div className="flex gap-1 overflow-x-auto hide-scrollbar flex-1 py-1">
              {slice.map(s => (
                <div key={s.n} className="shrink-0" style={{ width: 30, height: 30 }}>
                  <StickerCell sticker={s} {...cellProps} onClick={() => cellProps.onCellClick(s)} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   G — FAB picker + grid grande (mobile-fab)
   ────────────────────────────────────────────────────────────────────── */

function MobileFabPicker({ stickers, theme, cellProps, accent, muted, participating }) {
  const entries = useMemo(() => buildCountryEntries(stickers, participating), [stickers, participating]);
  const [idx, setIdx] = useState(0);
  const [sheet, setSheet] = useState(false);
  const [search, setSearch] = useState('');

  const active = entries[idx] || entries[0];
  if (!active) return null;

  const filtered = entries.filter(e => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const code = (e.kind === 'country' ? e.country.code : e.section.label).toLowerCase();
    const name = (e.kind === 'country' ? e.country.name : e.section.subtitle).toLowerCase();
    return code.includes(q) || name.includes(q);
  });

  return (
    <div className="px-3 pt-3 pb-24 relative">
      <div className="rounded-2xl overflow-hidden border" style={{ backgroundColor: theme?.gridBg ?? '#fff', borderColor: `${muted}22` }}>
        <div className="h-2" style={{ backgroundColor: active.kind === 'country' ? active.country.color : accent }} />
        <CountryHeaderInline entry={active} muted={muted} large />
        <StickerGrid slice={active.slice} columns={5} gap={6} padding="px-4 pb-5" cellProps={cellProps} theme={theme} />
      </div>

      <button
        onClick={() => setSheet(true)}
        className="fixed right-5 z-[70] flex items-center gap-2 px-4 py-3 rounded-full shadow-lg text-white font-bold text-sm"
        style={{
          backgroundColor: accent,
          bottom: 'calc(80px + var(--safe-area-bottom, 0px))',
        }}
      >
        <span className="material-symbols-outlined text-lg">public</span>
        Cambiar país
      </button>

      {sheet && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-end" onClick={() => setSheet(false)}>
          <div
            className="w-full max-h-[80vh] flex flex-col rounded-t-3xl bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1.5 rounded-full bg-gray-300 mx-auto mt-3 mb-2" />
            <div className="px-4 pt-1 pb-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar país..."
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
              />
            </div>
            <div className="overflow-y-auto px-3 pb-6 flex-1">
              <div className="grid grid-cols-1 gap-1.5">
                {filtered.map((e, i) => {
                  const eIdx = entries.indexOf(e);
                  const slice = e.slice;
                  const owned = ownedInSlice(slice);
                  const total = slice.length;
                  const pct = total ? Math.round((owned / total) * 100) : 0;
                  const color = e.kind === 'country' ? e.country.color : accent;
                  const code = e.kind === 'country' ? e.country.code : e.section.label;
                  const name = e.kind === 'country' ? e.country.name : e.section.subtitle;
                  const isActive = eIdx === idx;
                  return (
                    <button
                      key={i}
                      onClick={() => { setIdx(eIdx); setSheet(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left"
                      style={{ backgroundColor: isActive ? `${color}1a` : 'transparent', border: `1.5px solid ${isActive ? color : 'transparent'}` }}
                    >
                      <span className="rounded-sm shrink-0" style={{ backgroundColor: color, width: 4, height: 22 }} />
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-sm font-black truncate" style={{ color }}>{code}</span>
                        <span className="text-[11px] truncate" style={{ color: muted }}>{name}</span>
                      </div>
                      <span className="text-[10px] tabular-nums shrink-0" style={{ color: muted }}>{owned}/{total} · {pct}%</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   H — Híbrido: header collapsible de grupo + chips país + grid grande (mobile-hybrid)
   ────────────────────────────────────────────────────────────────────── */

function MobileHybrid({ stickers, theme, cellProps, accent, muted, participating }) {
  const groups = useMemo(() => buildGroupEntries(stickers, participating), [stickers, participating]);
  const [groupIdx, setGroupIdx] = useState(0);
  const [countryCode, setCountryCode] = useState(null);
  const [open, setOpen] = useState(true);

  const activeGroup = groups[groupIdx] || groups[0];
  if (!activeGroup) return null;

  const activeCountry = activeGroup.kind === 'group'
    ? (activeGroup.countries.find(c => c.code === countryCode) || activeGroup.countries[0])
    : null;

  const slice = activeCountry ? activeCountry.slice : activeGroup.slice;
  const headerColor = activeCountry?.color || accent;

  return (
    <div className="px-3 pt-3 pb-4">
      <div className="rounded-xl overflow-hidden border bg-white" style={{ borderColor: `${muted}33` }}>
        <button
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center gap-2 px-3 py-2.5"
        >
          <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: muted }}>Grupo</span>
          <span className="text-sm font-black" style={{ color: accent }}>{activeGroup.section.label}</span>
          <span className="text-xs truncate" style={{ color: muted }}>{activeGroup.section.subtitle}</span>
          <span className="ml-auto text-[10px] tabular-nums shrink-0" style={{ color: muted }}>
            {ownedInSlice(activeGroup.slice)}/{activeGroup.slice.length}
          </span>
          <span className="material-symbols-outlined text-base shrink-0" style={{ color: muted }}>{open ? 'expand_less' : 'expand_more'}</span>
        </button>
        {open && (
          <div className="px-2 pb-2 flex gap-1 overflow-x-auto hide-scrollbar">
            {groups.map((g, i) => {
              const isActive = i === groupIdx;
              return (
                <button
                  key={g.section.id}
                  onClick={() => { setGroupIdx(i); setCountryCode(null); }}
                  className="shrink-0 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest"
                  style={{
                    backgroundColor: isActive ? accent : `${accent}1a`,
                    color: isActive ? '#fff' : accent,
                  }}
                >
                  {g.section.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {activeGroup.kind === 'group' && (
        <div className="mt-2 grid grid-cols-4 gap-1.5">
          {activeGroup.countries.map(c => {
            const owned = ownedInSlice(c.slice);
            const total = c.slice.length;
            const pct = total ? Math.round((owned / total) * 100) : 0;
            const isActive = activeCountry?.code === c.code;
            return (
              <button
                key={c.code}
                onClick={() => setCountryCode(c.code)}
                className="flex flex-col items-stretch gap-0.5 px-1 py-1.5 rounded-lg transition-all"
                style={{
                  backgroundColor: isActive ? c.color : '#ffffff',
                  border: `1.5px solid ${isActive ? c.color : `${c.color}44`}`,
                }}
              >
                <span className="text-[12px] font-black tracking-wide leading-none" style={{ color: isActive ? '#fff' : c.color }}>{c.code}</span>
                <span className="text-[9px] tabular-nums leading-none" style={{ color: isActive ? '#ffffffcc' : muted }}>{owned}/{total}</span>
                <div className="h-1 rounded-full overflow-hidden mt-1" style={{ backgroundColor: isActive ? '#ffffff44' : `${c.color}22` }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: isActive ? '#fff' : c.color }} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-3 rounded-2xl overflow-hidden border" style={{ backgroundColor: theme?.gridBg ?? '#fff', borderColor: `${muted}22` }}>
        <div className="h-1.5" style={{ backgroundColor: headerColor }} />
        {activeCountry ? (
          <CountryHeaderInline entry={{ kind: 'country', country: activeCountry, slice, section: activeGroup.section }} muted={muted} large />
        ) : (
          <CountryHeaderInline entry={{ kind: 'fwc', section: activeGroup.section, slice }} muted={muted} large />
        )}
        <StickerGrid slice={slice} columns={5} gap={6} padding="px-4 pb-5" cellProps={cellProps} theme={theme} />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   Switch
   ────────────────────────────────────────────────────────────────────── */

export const MOBILE_LAYOUT_OPTIONS = [
  { id: 'mobile-cards-1',        shortLabel: '1-col',    labelKey: 'mobile_layout_one_col' },
  { id: 'mobile-cards-2',        shortLabel: '2-col',    labelKey: 'mobile_layout_two_col' },
  { id: 'mobile-cards-2-tight',  shortLabel: '2-col+',   labelKey: 'mobile_layout_two_col_tight' },
  { id: 'mobile-cards-2-xtight', shortLabel: '2-col++',  labelKey: 'mobile_layout_two_col_xtight' },
  { id: 'mobile-tabs',     shortLabel: 'Tabs',    labelKey: 'mobile_layout_tabs' },
  { id: 'mobile-double',   shortLabel: 'Doble',   labelKey: 'mobile_layout_double' },
  { id: 'mobile-snap-y',   shortLabel: 'Snap-V',  labelKey: 'mobile_layout_snap_y' },
  { id: 'mobile-swipe-x',  shortLabel: 'Swipe',   labelKey: 'mobile_layout_swipe_x' },
  { id: 'mobile-strips',   shortLabel: 'Strips',  labelKey: 'mobile_layout_strips' },
  { id: 'mobile-fab',      shortLabel: 'FAB',     labelKey: 'mobile_layout_fab' },
  { id: 'mobile-hybrid',   shortLabel: 'Híbrido', labelKey: 'mobile_layout_hybrid' },
];

function MobileAlbumGrid({ stickers, viewMode, selected, hideRepeated, extras, onCellClick, theme, layout, participatingStickers, searchQuery }) {
  const cellProps = { viewMode, selected, hideRepeated, extras, onCellClick, theme, participatingStickers };
  const accent = theme?.primaryAccent ?? '#10B981';
  const muted = theme?.mutedText ?? '#6b7280';
  const common = { stickers, theme, cellProps, accent, muted, participating: participatingStickers, searchQuery };

  switch (layout) {
    case 'mobile-cards-1':        return <MobileOneColCards {...common} />;
    case 'mobile-cards-2':        return <MobileTwoColCards {...common} />;
    case 'mobile-cards-2-tight':  return <MobileTwoColCardsTight {...common} />;
    case 'mobile-cards-2-xtight': return <MobileTwoColCardsXtight {...common} />;
    case 'mobile-tabs':    return <MobileTabsCards {...common} />;
    case 'mobile-double':  return <MobileDoubleSelector {...common} />;
    case 'mobile-snap-y':  return <MobileSnapVertical {...common} />;
    case 'mobile-swipe-x': return <MobileSwipeHorizontal {...common} />;
    case 'mobile-strips':  return <MobileStrips {...common} />;
    case 'mobile-fab':     return <MobileFabPicker {...common} />;
    case 'mobile-hybrid':  return <MobileHybrid {...common} />;
    default:               return <MobileOneColCards {...common} />;
  }
}

export default MobileAlbumGrid;
